import {HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom, Observable} from "rxjs";
import {PerFormaAPIService} from "src/app/services/pfapi.service";

// fetches binary endpoints through the generated client so the auth interceptor attaches the
// bearer token. a raw browser GET (<img src>, <a href>, window.open) carries no Authorization
// header and would 401 against the [Authorize] file/image endpoints — this routes them through
// HttpClient instead, then hands the resulting blob to the browser as an object URL.
@Injectable({providedIn: "root"})
export class FileDownloadService {
    // the generated option types don't expose responseType; the literal still carries it at runtime,
    // so the void-typed file endpoints (Document file, Report job/log files) return a Blob body.
    private readonly blobResponse = {observe: "response", responseType: "blob"} as {observe: "response"};

    constructor(
        private readonly api: PerFormaAPIService,
        private readonly message: NzMessageService
    ) {}

    // document file: inline preview (new tab) or attachment download
    openDocumentFile(id: number, inline = false): Promise<void> {
        return this.present(
            this.api.getApiDocumentFileId(id, {inline}, this.blobResponse) as unknown as Observable<HttpResponse<Blob>>,
            inline
        );
    }

    // PTP purchase-request attachment download (already blob-typed in the generated client)
    openPtpAttachment(attachmentId: number): Promise<void> {
        return this.present(this.api.getApiPTPGetPORequestAttachment({attachmentId}, {observe: "response"}), false);
    }

    // generated AR/FIN report output file download
    downloadReportJobFile(report: "ar" | "fin", jobGuid: string): Promise<void> {
        const call$ =
            report === "ar"
                ? this.api.getApiReportArJobsJobGuidFile(jobGuid, this.blobResponse)
                : this.api.getApiReportFinJobsJobGuidFile(jobGuid, this.blobResponse);
        return this.present(call$ as unknown as Observable<HttpResponse<Blob>>, false);
    }

    // AR/FIN report job log file (view in a new tab)
    viewReportJobLogFile(report: "ar" | "fin", jobGuid: string, logId: number): Promise<void> {
        const call$ =
            report === "ar"
                ? this.api.getApiReportArJobsJobGuidLogsLogIdFile(jobGuid, logId, this.blobResponse)
                : this.api.getApiReportFinJobsJobGuidLogsLogIdFile(jobGuid, logId, this.blobResponse);
        return this.present(call$ as unknown as Observable<HttpResponse<Blob>>, true);
    }

    private async present(response$: Observable<HttpResponse<Blob>>, inline: boolean): Promise<void> {
        let resp: HttpResponse<Blob>;
        try {
            resp = await lastValueFrom(response$);
        } catch (err) {
            // surface a missing file to the user so the caller's loader doesn't just vanish with no feedback;
            // anything else is an unexpected failure and stays on the global handler / AppInsights
            if (err instanceof HttpErrorResponse && err.status === 404) {
                this.message.error("File not found.");
                return;
            }
            throw err;
        }

        const blob = resp.body;
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        if (inline) {
            window.open(url, "_blank");
        } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = this.fileNameFromResponse(resp) ?? "";
            document.body.appendChild(a);
            a.click();
            a.remove();
        }

        // revoke once the browser has had time to load it from the object URL
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    }

    private fileNameFromResponse(resp: HttpResponse<Blob>): string | null {
        const disposition = resp.headers.get("Content-Disposition");
        if (!disposition) return null;
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
        return match ? decodeURIComponent(match[1]) : null;
    }
}
